import React, { useState, useMemo, useContext, useCallback, useEffect } from "react";

import {
    CardHeader,
    CardBody,
    Button,
    Row,
    Col
} from "reactstrap";

import { useTheme } from "../../../hooks/useTheme";

import DataTableWindowed from "../../../components/DataTableWindowed";
import { RoundToTwoDecimalPlaces } from "../../../utils/numberUtils";
import { NestQuoteItems, sanitizeForId } from "../functions/quoteFunctions";
import QuoteItemNameWithValidation from "./quoteItemNameWithValidation";
import QuoteItemGroupNameWithValidation from "./quoteItemGroupNameWithValidation";
import NestedTableQuoteItemColumn from "./NestedTableQuoteItemColumn";
import { useLockState } from "../../../components/userLocks/LockCheck";
import { QuoteDataContext } from "../contexts/quoteDataContext";
import QuoteItemBulkModal from "./quoteItemBulkModal";
import ItemTypeCustomAttributes from "../../items/components/ItemTypeCustomAttributes";
import { useQuoteItemTableUpdater } from "../../../hooks/useQuoteItemTableUpdater";
import ActionButton from "../../../components/actionButton";
import NestedTableSwitchColumn from "./NestedTableSwitchColumn";

function ModuleActionButtons({ row, onClickDecoupleModule, onClickDeleteModule, onClickEditModule }) {
    const [isDeletingModule, setIsDeletingModule] = useState(false);
    const theme = useTheme();

    return (
        <>
            {!isDeletingModule && <>
                <ActionButton
                    id={`decouple-btn-${sanitizeForId(row.quoteModuleGuid)}`}
                    color={theme === "light-mode" ? "light" : "dark"}
                    title="Decouple module"
                    icon="bi-hammer"
                    confirmTitle="Decouple Module Items?"
                    confirmMessage={
                        <div className="d-flex align-items-center">
                            <i className="bi bi-x-circle-fill text-danger fs-1 me-3"></i>
                            <div>
                                <div className="mb-2">Are you sure you want to decouple the items from the module?</div>
                                <br />
                                <div className="mb-2">This means you can fully edit the contained items, but you can no longer go back to edit the module and have it recalculate items</div>
                                <br />
                                <div className="mb-2">This action is <strong>irreversible</strong></div>
                            </div>
                        </div>
                    }
                    onClick={async () => await onClickDecoupleModule?.(row.quoteModuleGuid, row.description)}
                    confirmButtonLabel="Decouple"
                    confirmButtonColor="warning"
                />
                <Button
                    id={`edit-btn-${sanitizeForId(row.guid)}`}
                    color="secondary"
                    outline
                    title="Edit module"
                    onClick={() => onClickEditModule?.(row.guid)}
                >
                    <i className="bi bi-pencil"></i>
                </Button>
            </>
            }
            <ActionButton
                id={`delete-btn-${sanitizeForId(row.guid)}`}
                color="danger"
                title="Delete module"
                icon="bi-trash"
                onClick={async () => {
                    setIsDeletingModule(true);
                    try {
                        await onClickDeleteModule?.(row.guid);
                    } finally {
                        setIsDeletingModule(false);
                    }
                }}
            />
        </>
    );
}

const QuoteCartTable = ({
    quoteItems,
    onClickEditModule,
    onClickDeleteModule,
    onClickDecoupleModule,
    onClickEditItem,
    onClickDeleteItem,
    onClickDeleteItems,
    onClickAddCustomItem,
    layoutMode,
    setLayoutMode,
    updateQuoteItem,
    quoteModules,
    updateQuoteItems,
    updateQuoteItemTypeAttribute
}) => {
    const theme = useTheme();
    const [currentTab, setCurrentTab] = useState('Basket');
    const [editRow] = useQuoteItemTableUpdater(quoteItems, updateQuoteItem, updateQuoteItems);

    const { itemLookupTypes, suppliers, salesforceCodes } = useContext(QuoteDataContext);

    const lockState = useLockState();


    const filteredItems = useMemo(() => {
        return quoteItems.filter(x =>
            currentTab === "Basket" ||
            currentTab === "All Configuration" ||
            (x.itemLookupTypeId === (itemLookupTypes ?? []).find(ilt => ilt.itemLookupTypeName === currentTab)?.id)
        )
    }, [quoteItems, currentTab, itemLookupTypes]);

    const formattedData = useMemo(() => NestQuoteItems(filteredItems, quoteModules, salesforceCodes), [filteredItems, quoteModules, salesforceCodes]);    

    const [bulkModalOpen, setBulkModalOpen] = useState(false);
    const [selectedIds, setSelectedIds] = useState(new Set());

    // Derive selected items from live quoteItems so bulk modal always gets up-to-date data
    const selectedItems = useMemo(
        () => quoteItems.filter(x => selectedIds.has(x.id)),
        [quoteItems, selectedIds]
    );

    const [isUpdating, setIsUpdating] = useState(false);

    const handleBulkSave = useCallback(async (updatedRows) => {
        setIsUpdating(true);
        try {
            await updateQuoteItems?.(updatedRows.map(row => ({ ...row })));
        } catch (error) {
            // Ensure errors from bulk update are handled and don't become unhandled rejections
            // Replace with user-facing reporting (e.g. toast) if available in this project
            // eslint-disable-next-line no-console
            console.error("Failed to update quote items in handleBulkSave:", error);
        } finally {
            setIsUpdating(false);
        }
    }, [updateQuoteItems]);    

    const onClickDeleteItemGroup = useCallback(async (items) => {
        await onClickDeleteItems(items);
    }, [onClickDeleteItems]);

    const onClickEmptyBasket = useCallback(async () => {
        await onClickDeleteItems?.(quoteItems);
    }, [onClickDeleteItems, quoteItems]);

    const handleCustomAttributeChange = useCallback((row, e, type) => {
        const name = e.target.name;
        const newValue = type === 'boolean' ? e.target.checked : e.target.value;

        updateQuoteItemTypeAttribute(row.databaseId, name, newValue);
    }, [updateQuoteItemTypeAttribute]);

    const customAttributeColumns = useMemo(() => {
        if (!itemLookupTypes) return [];
        const attrTypeMap = new Map();
        quoteItems.forEach(f => {
            if (f.itemLookupTypeId !== 0 && f.itemLookupTypeId !== null) {
                const itemType = itemLookupTypes.find(f3 => f3.id === f.itemLookupTypeId);
                if (itemType?.customAttributes) {
                    itemType.customAttributes
                        .forEach(f2 => {
                        if (!attrTypeMap.has(f2.label)) {
                            attrTypeMap.set(f2.label, new Set());
                        }
                        attrTypeMap.get(f2.label).add(itemType.itemLookupTypeName);
                    });
                }
            }
        });
        const dedupe = Array.from(attrTypeMap.entries()).map(([label, typeNames]) => ({
            label,
            value: label,
            itemLookupTypes: Array.from(typeNames)
        }));

        const getAttributeColumnWidth = (label) => {  
            const customAttr = itemLookupTypes.find(ilt => ilt.customAttributes?.some(f => f.label === label))?.customAttributes.find(f => f.label === label);
            const fieldType = customAttr?.fieldType;
            let widthBasedOnHeader = label.length * 3.2; // Estimate width based on header text length.  Aiming to have it wrap to three lines max

            switch (fieldType) {
                case "boolean":
                    return Math.max(widthBasedOnHeader, 80) + "px";
                case "datetime":
                    return Math.max(widthBasedOnHeader, 200) + "px";
                case "decimal":
                    return Math.max(widthBasedOnHeader, 100) + "px";
                case "integer":
                    return Math.max(widthBasedOnHeader, 80) + "px";
                case "lookup": {
                    const choices = customAttr?.lookupChoices ?? [];
                    const maxLen = choices.reduce((max, c) => Math.max(max, c?.length ?? 0), 0);

                    const baseWidth = 60; // base width for dropdown arrow and padding
                    const pixelsPerCharacter = 7; // approximate width of a character in pixels, not exact as each character can have different width but good enough for estimation
                    const spaceForCharacters = maxLen * pixelsPerCharacter;
                    const minWidth = 120;
                    const maxWidth = 350;

                    const width = Math.min(Math.max(spaceForCharacters + baseWidth, minWidth), maxWidth);
                    return `${Math.max(widthBasedOnHeader, width)}px`;
                }
                case "text":
                    return Math.max(widthBasedOnHeader, 250) + "px";
                default:
                    return undefined;
            }
        }

        return dedupe.map((m) => ({
            key: "TypeCustomAttr_" + m.label,
            label: m.label,
            itemLookupTypes: m.itemLookupTypes,
            isSortDisabled: true,
            isDefault: true,
            group: "Attributes",
            style: { whiteSpace: "normal", width: getAttributeColumnWidth(m.label) },
            render: (_, row) => {
                const itemLookupType = itemLookupTypes.find(ilt => ilt.id === row.itemLookupTypeId);
                if (!itemLookupType?.customAttributes) return "-";
                const customAttribute = itemLookupType.customAttributes.find(f => f.label === m.label);
                const value = row.typeCustomAttributes?.find(f => f.label === m.label)?.value ?? null;

                return customAttribute
                    ? <ItemTypeCustomAttributes 
                        label={m.label} 
                        value={value} 
                        handleChange={(e, type) => handleCustomAttributeChange(row, e, type)} 
                        lookupChoices={customAttribute.lookupChoices} 
                        required={customAttribute.required} 
                        type={customAttribute.fieldType} 
                        disabled={row.source?.toLocaleLowerCase() === "basket processing" || lockState?.isLocked || row.quoteModuleGuid || (!customAttribute.userCanEdit && row.source !== "Custom")} 
                        title={row.quoteModuleGuid ? "Cannot edit custom attributes for module items" : row.source?.toLocaleLowerCase() === "basket processing" ? "Cannot edit custom attributes for items from basket processing" : row.source !== "Custom" && !customAttribute.userCanEdit ? "This attribute can only be changed for non-catalogue items" : undefined}
                    />
                    : "-";
            }
        }));
    }, [quoteItems, itemLookupTypes, handleCustomAttributeChange, lockState?.isLocked]);    

    const itemLookupTypeMap = useMemo(
        () => new Map((itemLookupTypes ?? []).map(t => [t.id, t.itemLookupTypeName])),
        [itemLookupTypes]
    );
    const supplierMap = useMemo(
        () => new Map((suppliers ?? []).map(s => [s.id, s.supplierName])),
        [suppliers]
    );

    const mainColumns = useMemo(() => [
        {
            key: "source",
            label: "Source Icon",
            header: "Source",   
            isFixed: true,  
            style: { width: "50px" },          
            render: (value, row) => {
                return <NestedTableQuoteItemColumn row={row} columnKey="source" />;
            }
        },
        {
            key: "itemNumber",
            label: "Item #",
            header: "#",
            dataType: "number",
            align: "left",
            showInFooter: false,
            style: { width: "20px" },
            isFixed: true,
            render: (value, row) => {
                return <div style={{width:"100%", textAlign: "right"}}>{value}</div>
            }
        },
        {
            key: "description",
            label: "Name",
            header: <span><i className={`bi bi-exclamation-triangle me-2`} style={{ visibility: "hidden" }}></i>Name</span>, /* Hidden icon is just there for spacing reasons, as the data rows can have an icon before the name */
            align: "left",
            style: { width: "300px" },
            showInFooter: false,
            isFixed: true,
            isSystem: true,
            dataType: "text",
            render: (value, row, isExpanded) => {
                return (
                    <>
                        {!row.topLevelGroupRow &&
                            <QuoteItemNameWithValidation item={row} editRow={editRow} />
                        }
                        {row.topLevelGroupRow &&
                            <QuoteItemGroupNameWithValidation group={row} />
                        }
                    </>
                )
            }
        },
        {
            key: "nameOnQuote",
            label: "Name on Quote",
            align: "left",
            style: { width: "300px" },
            showInFooter: false,
            isFixed: true,
            dataType: "text",
            searchValue: (value, row) => {
                if (row.isUpfront && row.isRecurring) {
                    return `${row.upfrontNameOnQuote ?? ""} ${row.recurringNameOnQuote ?? ""}`
                } else if (row.isUpfront) {
                    return row.upfrontNameOnQuote ?? ""
                } else if (row.isRecurring) {
                    return row.recurringNameOnQuote ?? ""
                }
                return ""
            },
            render: (value, row, isExpanded) => {
                const fieldName = "nameOnQuote";
                return (
                    <NestedTableQuoteItemColumn
                        row={row}
                        columnKey={fieldName}
                        onChange={(e) => editRow(row, e.target.name, e.target.value)}
                    />
                )
            }
        },
        {
            key: "autotaskCode",
            label: "Autotask Code",
            align: "left",
            style: { width: "250px" },
            showInFooter: false,
            dataType: "text",
            render: (value, row, isExpanded) => {
                const fieldName = "autotaskCode";
                return (
                    <NestedTableQuoteItemColumn
                        row={row}
                        columnKey={fieldName}
                        onChange={(e) => editRow(row, fieldName, e.target.value)}
                    />
                )
            }
        },
        {
            key: "salesforceCode",
            label: "Salesforce Code",
            align: "left",
            style: { width: "350px" },
            showInFooter: false,
            dataType: "text",
            searchValue: (value, row) => {
                if (row.isUpfront && row.isRecurring) {
                    return `${row.upfrontSalesforceCode ?? ""} ${row.recurringSalesforceCode ?? ""}`
                } else if (row.isUpfront) {
                    return row.upfrontSalesforceCode ?? ""
                } else if (row.isRecurring) {
                    return row.recurringSalesforceCode ?? ""
                }
                return ""
            },
            render: (value, row, isExpanded) => {
               const fieldName = "salesforceCode";
                return (
                    <NestedTableQuoteItemColumn
                        row={row}
                        columnKey={fieldName}
                        onChange={(e) => editRow(row, e.target.name, e.target.value)}
                    />
                )
             }
        },
        {
            key: "itemLookupTypeId",
            label: "Type",
            align: "left",
            style: { width: "280px" },
            showInFooter: false,
            dataType: "text",
            searchValue: (value) => itemLookupTypeMap.get(value) ?? "",
            render: (value, row) => {
               const fieldName = "itemLookupTypeId";
                return (
                    <NestedTableQuoteItemColumn
                        row={row}
                        columnKey={fieldName}
                        onChange={(e) => editRow(row, fieldName, e.target.value)}
                    />
                )            }
        },
        {
            key: "quantity",
            label: "Quantity",
            style: { width: "80px" },
            showInFooter: false,
            dataType: "number",
            isDefault: true,
            render: (value, row, isExpanded) => {
                const fieldName = "quantity"
                return (
                    <NestedTableQuoteItemColumn
                        row={row}
                        columnKey={fieldName}
                        disabled={row.quoteModuleGuid || row.source === "Basket Processing"}
                        onChange={(e) => editRow(row, fieldName, e.target.value)}
                    />
                )
            }
        },
        {
            key: "supplierId",
            label: "Supplier",
            style: { width: "300px" },
            align: "left",
            dataType: "text",
            searchValue: (value) => supplierMap.get(value) ?? "",
            render: (value, row, isExpanded) => {
               const fieldName = "supplierId";
                return (
                    <NestedTableQuoteItemColumn
                        row={row}
                        columnKey={fieldName}
                        onChange={(e) => editRow(row, fieldName, e.target.value)}
                    />
                ) 
            } 
        }
    ].map(x => ({ ...x, group: "Properties" })), [editRow, itemLookupTypeMap, supplierMap]);

    const upfrontColumns = useMemo(() => [
        {
            key: "originalUpfrontCost",
            label: "Unit Upfront List Cost",
            header: <>Unit List Cost</>,
            showInFooter: false,
            dataType: "currency",
            render: (value, row, isExpanded) => {
                const fieldName = "originalUpfrontCost";
                return (
                    <NestedTableQuoteItemColumn
                        row={row}
                        columnKey={fieldName}
                        disabled={row.itemLookupId || !row.isUpfront}
                        onChange={(e) => editRow(row, fieldName, e.target.value)}
                    />
                )
            }
        },
        {
            key: "upfrontCost",
            label: "Unit Upfront Cost",
            header: <>Unit Cost</>,
            showInFooter: false,
            dataType: "currency",
            isDefault: true,
            render: (value, row, isExpanded) => {
                const fieldName = "upfrontCost";
                return (
                    <NestedTableQuoteItemColumn
                        row={row}
                        columnKey={fieldName}
                        onChange={(e) => editRow(row, fieldName, e.target.value)}
                    />
                )
            }
        },
        {
            key: "originalUpfrontPrice",
            label: "Unit Upfront List Sell",
            header: <>Unit List Sell</>,
            showInFooter: false,
            dataType: "currency",
            render: (value, row, isExpanded) => {
                const fieldName = "originalUpfrontPrice";
                return (
                    <NestedTableQuoteItemColumn
                        row={row}
                        columnKey={fieldName}
                        disabled={row.itemLookupId || !row.isUpfront}
                        onChange={(e) => editRow(row, fieldName, e.target.value)}
                    />
                )
            }
        },
        {
            key: "upfrontPrice",
            label: "Unit Upfront Sell",
            header: <>Unit Sell</>,
            showInFooter: false,
            dataType: "currency",
            isDefault: true,
            render: (value, row, isExpanded) => {
                const fieldName = "upfrontPrice";
                return (
                    <NestedTableQuoteItemColumn
                        row={row}
                        columnKey={fieldName}
                        onChange={(e) => editRow(row, fieldName, e.target.value)}
                    />
                )
            }
        },
        {
            key: "upfrontMarginValue",
            label: "Unit Upfront Margin £",
            header: <>£<br />Unit Margin</>,
            showInFooter: false,
            dataType: "currency",
            render: (value, row, isExpanded) => {
                return (
                    <NestedTableQuoteItemColumn
                        row={row}
                        columnKey={"upfrontMarginValue"}
                        disabled={true}
                    />
                )
            }
        },
        {
            key: "upfrontMargin",
            label: "Unit Upfront Margin %",
            header: <>%<br />Unit Margin</>,
            className: "p-1",
            style: { minWidth: "110px", maxWidth: "110px" },
            showInFooter: false,
            dataType: "percentage",
            render: (value, row, isExpanded, hasChildren) => {
                if (!hasChildren) {
                    return (
                        <NestedTableQuoteItemColumn
                            row={row}
                            columnKey={"upfrontMargin"}
                            disabled={true}
                        />
                    )
                }
            }
        },
        {
            key: "upfrontPriceDiscountPercent",
            label: "Unit Upfront Sell Discount %",
            header: <>%<br />Unit Sell Discount</>,
            className: "p-1",
            style: { minWidth: "110px", maxWidth: "110px" },
            showInFooter: false,
            dataType: "percentage",
            render: (value, row, isExpanded, hasChildren) => {
                if (!hasChildren) {
                    return (
                        <NestedTableQuoteItemColumn
                            row={row}
                            columnKey={"upfrontPriceDiscountPercent"}
                            disabled={true}
                        />
                    )
                }
            }
        },
        {
            key: "upfrontPriceDiscountValue",
            label: "Unit Upfront Sell Discount £",
            header: <>£<br />Unit Sell Discount</>,
            showInFooter: false,
            dataType: "currency",
            render: (value, row, isExpanded, hasChildren) => {
                if (!hasChildren) {
                    return (
                        <NestedTableQuoteItemColumn
                            row={row}
                            columnKey={"upfrontPriceDiscountValue"}
                            disabled={true}
                        />
                    )
                }
            }
        },
        {
            key: "totalOriginalUpfrontCost",
            label: "Total Upfront List Cost",
            header: <>Total List Cost</>,
            canBeTotalled: true,
            dataType: "currency",
            render: (value, row, isExpanded) => {
                return (
                    <NestedTableQuoteItemColumn
                        row={row}
                        columnKey={"totalOriginalUpfrontCost"}
                        disabled={true}
                        hidden={isExpanded}
                    />
                )
            }
        },
        {
            key: "totalUpfrontCost",
            label: "Total Upfront Cost",
            header: <>Total Cost</>,
            canBeTotalled: true,
            dataType: "currency",
            isDefault: true,
            render: (value, row, isExpanded) => {
                return (
                    <NestedTableQuoteItemColumn
                        row={row}
                        columnKey={"totalUpfrontCost"}
                        disabled={true}
                        hidden={isExpanded}
                    />
                )
            }
        },
        {
            key: "totalOriginalUpfrontPrice",
            label: "Total Upfront List Sell",
            header: <>Total List Sell</>,
            canBeTotalled: true,
            dataType: "currency",
            render: (value, row, isExpanded) => {
                return (
                    <NestedTableQuoteItemColumn
                        row={row}
                        columnKey={"totalOriginalUpfrontPrice"}
                        disabled={true}
                        hidden={isExpanded}
                    />
                )
            }
        },
        {
            key: "totalUpfrontPrice",
            label: "Total Upfront Sell",
            header: <>Total Sell</>,
            canBeTotalled: true,
            dataType: "currency",
            isDefault: true,
            render: (value, row, isExpanded) => {
                return (
                    <NestedTableQuoteItemColumn
                        row={row}
                        columnKey={"totalUpfrontPrice"}
                        disabled={true}
                        hidden={isExpanded}
                    />
                )
            }
        },
        {
            key: "totalUpfrontMarginValue",
            label: "Total Upfront Margin £",
            header: <>£<br />Total Margin</>,
            canBeTotalled: true,
            dataType: "currency",
            render: (value, row, isExpanded) => {
                return (
                    <NestedTableQuoteItemColumn
                        row={row}
                        columnKey={"totalUpfrontMarginValue"}
                        disabled={true}
                        hidden={isExpanded}
                    />
                )
            }
        },
        {
            key: "totalUpfrontMargin",
            label: "Total Upfront Margin %",
            header: <>%<br />Total Margin</>,
            className: "p-1",
            style: { minWidth: "110px", maxWidth: "110px" },
            canBeTotalled: false,
            dataType: "percentage",
            calcFooter: (footer, allData) => {
                return footer.totalUpfrontPrice > 0 ? RoundToTwoDecimalPlaces((footer.totalUpfrontMarginValue / footer.totalUpfrontPrice) * 100) : 0
            },
            render: (value, row, isExpanded) => {
                return (
                    <NestedTableQuoteItemColumn
                        row={row}
                        columnKey={"totalUpfrontMargin"}
                        disabled={true}
                        hidden={isExpanded}
                    />
                )
            }
        },
        {
            key: "totalUpfrontPriceDiscountPercent",
            label: "Total Upfront Sell Discount %",
            header: <>%<br />Total Sell<br />Discount</>,
            className: "p-1",
            style: { minWidth: "110px", maxWidth: "110px" },
            canBeTotalled: false,
            dataType: "percentage",
            calcFooter: (footer, allData) => {
                if (footer.totalUpfrontPriceDiscountValue <= 0 || footer.totalOriginalUpfrontPrice === 0) return 0;

                return ((footer.totalUpfrontPriceDiscountValue / footer.totalOriginalUpfrontPrice) * 100).toFixed(2)
            },
            render: (value, row, isExpanded) => {
                return (
                    <NestedTableQuoteItemColumn
                        row={row}
                        columnKey={"totalUpfrontPriceDiscountPercent"}
                        disabled={true}
                        hidden={isExpanded}
                    />
                )
            }
        },
        {
            key: "totalUpfrontPriceDiscountValue",
            label: "Total Upfront Sell Discount £",
            header: <>£<br />Total Sell<br />Discount</>,
            canBeTotalled: true,
            dataType: "currency",
            render: (value, row, isExpanded) => {
                return (
                    <NestedTableQuoteItemColumn
                        row={row}
                        columnKey={"totalUpfrontPriceDiscountValue"}
                        disabled={true}
                        hidden={isExpanded}
                    />
                )
            }
        }
    ].map(x => ({
        ...x, group: "Upfront",
        className: "p-1",
        style: { ...x.style, minWidth: x.style?.minWidth || "165px", maxWidth: x.style?.maxWidth || "165px" }
    })), [editRow]);

    const billingScheduleColumns = useMemo(() => [
        {
            key: "recurringStartMonth",
            label: "Start Month",
            header: <>Start Month</>,
            className: "p-1",
            style: { minWidth: "110px", maxWidth: "110px" },
            showInFooter: false,
            dataType: "number",
            render: (value, row, isExpanded) => {
                const fieldName = "recurringStartMonth"
                return (
                    <NestedTableQuoteItemColumn
                        row={row}
                        columnKey={fieldName}
                        onChange={(e) => editRow(row, fieldName, e.target.value)}
                    />
                )
            }
        },
        {
            key: "recurringIntervalMonths",
            label: "Interval Months",
            header: <>Interval Months</>,
            className: "p-1",
            style: { minWidth: "110px", maxWidth: "110px" },
            showInFooter: false,
            dataType: "number",
            render: (value, row, isExpanded) => {
                const fieldName = "recurringIntervalMonths"
                return (
                    <NestedTableQuoteItemColumn
                        row={row}
                        columnKey={fieldName}
                        onChange={(e) => editRow(row, fieldName, e.target.value)}
                    />
                )
            }
        },
        {
            key: "recurringNumPayments",
            label: "Num Payments",
            header: <>Num Payments</>,
            className: "p-1",
            style: { minWidth: "110px", maxWidth: "110px" },
            showInFooter: false,
            dataType: "number",
            render: (value, row, isExpanded) => {
                const fieldName = "recurringNumPayments"
                return (
                    <NestedTableQuoteItemColumn
                        row={row}
                        columnKey={fieldName}
                        onChange={(e) => editRow(row, fieldName, e.target.value)}
                    />
                )
            }
        },
        {
            key: "startDelayDescription",
            label: "Start Delay",
            header: <>Start Delay</>,
            className: "p-1",
            align: "right",
            style: { minWidth: "80px", maxWidth: "80px" },
            showInFooter: false,
            dataType: "text"
        },
        {
            key: "intervalDescription",
            label: "Interval Description",
            header: <>Interval Description</>,
            className: "p-1",
            align: "right",
            style: { minWidth: "80px", maxWidth: "80px" },
            showInFooter: false,
            dataType: "text"
        },
        {
            key: "billingDescription",
            label: "Billing Description",
            header: <>Billing Description</>,
            className: "p-1",
            align: "right",
            style: { minWidth: "80px", maxWidth: "80px" },
            showInFooter: false,
            dataType: "text"
        },
        {
            key: "termLengthDescription",
            label: "Term Length",
            header: <>Term Length</>,
            className: "p-1",
            align: "right",
            style: { minWidth: "110px", maxWidth: "110px" },
            canBeTotalled: false,
            showInFooter: false,
            dataType: "text"
        },
        {
            key: "upfrontRecognitionMonths",
            label: "Upfront Recognition Months",
            header: <>Upfront Recognition Months</>,
            className: "p-1",
            align: "right",
            style: { minWidth: "110px", maxWidth: "110px" },
            canBeTotalled: false,
            showInFooter: false,
            dataType: "number",
            render: (value, row, isExpanded) => {
                const fieldName = "upfrontRecognitionMonths"
                return (
                    <NestedTableQuoteItemColumn
                        row={row}
                        columnKey={fieldName}
                        onChange={(e) => editRow(row, fieldName, e.target.value)}
                    />
                )
            }
        }
    ].map(x => ({
        ...x, group: "Billing Schedule",
        className: "p-1",
        style: { ...x.style, minWidth: x.style?.minWidth || "165px", maxWidth: x.style?.maxWidth || "165px" }
    })), [editRow]);

    const recurringColumns = useMemo(() => [
        {
            key: "originalRecurringCost",
            label: "Unit Recurring List Cost",
            header: <>Unit List Cost</>,
            showInFooter: false,
            dataType: "currency",
            render: (value, row, isExpanded) => {
                const fieldName = "originalRecurringCost";
                return (
                    <NestedTableQuoteItemColumn
                        row={row}
                        columnKey={fieldName}
                        disabled={row.itemLookupId || !row.isRecurring}
                        onChange={(e) => editRow(row, fieldName, e.target.value)}
                    />
                )
            }
        },
        {
            key: "recurringCost",
            label: "Unit Recurring Cost",
            header: <>Unit Cost</>,
            showInFooter: false,
            dataType: "currency",
            isDefault: true,
            render: (value, row, isExpanded) => {
                const fieldName = "recurringCost";
                return (
                    <NestedTableQuoteItemColumn
                        row={row}
                        columnKey={fieldName}
                        onChange={(e) => editRow(row, fieldName, e.target.value)}
                    />
                )
            }
        },
        {
            key: "originalRecurringPrice",
            label: "Unit Recurring List Sell",
            header: <>Unit List Sell</>,
            showInFooter: false,
            dataType: "currency",
            render: (value, row, isExpanded) => {
                const fieldName = "originalRecurringPrice";
                return (
                    <NestedTableQuoteItemColumn
                        row={row}
                        columnKey={fieldName}
                        disabled={row.itemLookupId || !row.isRecurring}
                        onChange={(e) => editRow(row, fieldName, e.target.value)}
                    />
                )
            }
        },
        {
            key: "recurringPrice",
            label: "Unit Recurring Sell",
            header: <>Unit Sell</>,
            className: "p-1 fixed-col-100",
            showInFooter: false,
            dataType: "currency",
            isDefault: true,
            render: (value, row, isExpanded) => {
                const fieldName = "recurringPrice";
                return (
                    <NestedTableQuoteItemColumn
                        row={row}
                        columnKey={fieldName}
                        onChange={(e) => editRow(row, fieldName, e.target.value)}
                    />
                )
            }
        },
        {
            key: "recurringMarginValue",
            label: "Unit Recurring Margin £",
            header: <>£<br />Unit Margin</>,
            showInFooter: false,
            dataType: "currency",
            render: (value, row, isExpanded) => {
                return (
                    <NestedTableQuoteItemColumn
                        row={row}
                        columnKey={"recurringMarginValue"}
                        disabled={true}
                    />
                )
            }
        },
        {
            key: "recurringMargin",
            label: "Unit Recurring Margin %",
            header: <>%<br />Unit Margin</>,
            className: "p-1",
            style: { minWidth: "110px", maxWidth: "110px" },
            showInFooter: false,
            dataType: "percentage",
            render: (value, row, isExpanded, hasChildren) => {
                if (!hasChildren) {
                    return (
                        <NestedTableQuoteItemColumn
                            row={row}
                            columnKey={"recurringMargin"}
                            disabled={true}
                        />
                    )
                }
            }
        },
        {
            key: "recurringPriceDiscountPercent",
            label: "Unit Recurring Sell Discount %",
            header: <>%<br />Unit Sell Discount</>,
            className: "p-1",
            style: { minWidth: "110px", maxWidth: "110px" },
            showInFooter: false,
            dataType: "percentage",
            render: (value, row, isExpanded, hasChildren) => {
                if (!hasChildren) {
                    return (
                        <NestedTableQuoteItemColumn
                            row={row}
                            columnKey={"recurringPriceDiscountPercent"}
                            disabled={true}
                        />
                    )
                }
            }
        },
        {
            key: "recurringPriceDiscountValue",
            label: "Unit Recurring Sell Discount £",
            header: <>£<br />Unit Sell Discount</>,
            showInFooter: false,
            dataType: "currency",
            render: (value, row, isExpanded, hasChildren) => {
                if (!hasChildren) {
                    return (
                        <NestedTableQuoteItemColumn
                            row={row}
                            columnKey={"recurringPriceDiscountValue"}
                            disabled={true}
                        />
                    )
                }
            }
        },
        {
            key: "totalOriginalRecurringCost",
            label: "Total Recurring List Cost",
            header: <>Total List Cost</>,
            canBeTotalled: true,
            dataType: "currency",
            render: (value, row, isExpanded) => {
                return (
                    <NestedTableQuoteItemColumn
                        row={row}
                        columnKey={"totalOriginalRecurringCost"}
                        disabled={true}
                        hidden={isExpanded}
                    />
                )
            }
        },
        {
            key: "totalRecurringCost",
            label: "Total Recurring Cost",
            header: <>Total Cost</>,
            canBeTotalled: true,
            dataType: "currency",
            isDefault: true,
            render: (value, row, isExpanded) => {
                return (
                    <NestedTableQuoteItemColumn
                        row={row}
                        columnKey={"totalRecurringCost"}
                        disabled={true}
                        hidden={isExpanded}
                    />
                )
            }
        },
        {
            key: "totalOriginalRecurringPrice",
            label: "Total Recurring List Sell",
            header: <>Total List Sell</>,
            canBeTotalled: true,
            dataType: "currency",
            isDefault: true,
            render: (value, row, isExpanded) => {
                return (
                    <NestedTableQuoteItemColumn
                        row={row}
                        columnKey={"totalOriginalRecurringPrice"}
                        disabled={true}
                        hidden={isExpanded}
                    />
                )
            }
        },
        {
            key: "totalRecurringPrice",
            label: "Total Recurring Sell",
            header: <>Total Sell</>,
            canBeTotalled: true,
            dataType: "currency",
            render: (value, row, isExpanded) => {
                return (
                    <NestedTableQuoteItemColumn
                        row={row}
                        columnKey={"totalRecurringPrice"}
                        disabled={true}
                        hidden={isExpanded}
                    />
                )
            }
        },
        {
            key: "totalRecurringMarginValue",
            label: "Total Recurring Margin £",
            header: <>£<br />Total Margin</>,
            canBeTotalled: true,
            dataType: "currency",
            render: (value, row, isExpanded) => {
                return (
                    <NestedTableQuoteItemColumn
                        row={row}
                        columnKey={"totalRecurringMarginValue"}
                        disabled={true}
                        hidden={isExpanded}
                    />
                )
            }
        },
        {
            key: "totalRecurringMargin",
            label: "Total Recurring Margin %",
            header: <>%<br />Total Margin</>,
            className: "p-1",
            style: { minWidth: "110px", maxWidth: "110px" },
            dataType: "percentage",
            calcFooter: (footer, allData) => {
                return footer.totalRecurringPrice > 0 ? RoundToTwoDecimalPlaces((footer.totalRecurringMarginValue / footer.totalRecurringPrice) * 100) : 0
            },
            render: (value, row, isExpanded) => {
                return (
                    <NestedTableQuoteItemColumn
                        row={row}
                        columnKey={"totalRecurringMargin"}
                        disabled={true}
                        hidden={isExpanded}
                    />
                )
            }
        },
        {
            key: "totalRecurringPriceDiscountPercent",
            label: "Total Recurring Sell Discount %",
            header: <>%<br />Total Sell<br />Discount</>,
            className: "p-1",
            style: { minWidth: "110px", maxWidth: "110px" },
            dataType: "percentage",
            calcFooter: (footer, allData) => {
                return footer.totalOriginalRecurringPrice
                    ? ((RoundToTwoDecimalPlaces(footer.totalOriginalRecurringPrice - footer.totalRecurringPrice) / footer.totalOriginalRecurringPrice) * 100).toFixed(2)
                    : 0
            },
            render: (value, row, isExpanded) => {
                return (
                    <NestedTableQuoteItemColumn
                        row={row}
                        columnKey={"totalRecurringPriceDiscountPercent"}
                        disabled={true}
                        hidden={isExpanded}
                    />
                )
            }
        },
        {
            key: "totalRecurringPriceDiscountValue",
            label: "Total Recurring Sell Discount £",
            header: <>£<br />Total Sell<br />Discount</>,
            canBeTotalled: true,
            dataType: "currency",
            render: (value, row, isExpanded) => {
                return (
                    <NestedTableQuoteItemColumn
                        row={row}
                        columnKey={"totalRecurringPriceDiscountValue"}
                        disabled={true}
                        hidden={isExpanded}
                    />
                )
            }
        }
    ].map(x => ({
        ...x, group: "Recurring",
        className: "p-1",
        style: { ...x.style, minWidth: x.style?.minWidth || "165px", maxWidth: x.style?.maxWidth || "165px" }
    })), [editRow]);

    const annualColumns = useMemo(() => [
        {
            key: "annualCost",
            label: "Unit Annual Cost",
            header: <>Unit Cost</>,
            showInFooter: false,
            dataType: "currency",
            render: (value, row, isExpanded) => {
                return (
                    <NestedTableQuoteItemColumn
                        row={row}
                        columnKey={"annualCost"}
                        disabled={true}
                        hidden={isExpanded}
                    />
                )
            }
        },
        {
            key: "annualPrice",
            label: "Unit Annual Sell",
            header: <>Unit Sell</>,
            showInFooter: false,
            dataType: "currency",
            render: (value, row, isExpanded) => {
                return (
                    <NestedTableQuoteItemColumn
                        row={row}
                        columnKey={"annualPrice"}
                        disabled={true}
                        hidden={isExpanded}
                    />
                )
            }
        },
        {
            key: "annualMargin",
            label: "Unit Annual Margin %",
            header: <>%<br />Unit Margin</>,
            className: "p-1",
            style: { minWidth: "110px", maxWidth: "110px" },
            showInFooter: false,
            dataType: "percentage",
            render: (value, row, isExpanded, hasChildren) => {
                if (!hasChildren) {
                    return (
                        <NestedTableQuoteItemColumn
                            row={row}
                            columnKey={"annualMargin"}
                            disabled={true}
                        />
                    )
                }
            }
        },
        {
            key: "annualMarginValue",
            label: "Unit Annual Margin £",
            header: <>£<br />Unit Margin</>,
            showInFooter: false,
            dataType: "currency",
            render: (value, row, isExpanded) => {
                return (
                    <NestedTableQuoteItemColumn
                        row={row}
                        columnKey={"annualMarginValue"}
                        disabled={true}
                    />
                )
            }
        },
        {
            key: "totalAnnualCost",
            label: "Total Annual Cost",
            header: <>Total Cost</>,
            canBeTotalled: true,
            dataType: "currency",
            render: (value, row, isExpanded) => {
                return (
                    <NestedTableQuoteItemColumn
                        row={row}
                        columnKey={"totalAnnualCost"}
                        disabled={true}
                        hidden={isExpanded}
                    />
                )
            }
        },
        {
            key: "totalAnnualPrice",
            label: "Total Annual Sell",
            header: <>Total Sell</>,
            canBeTotalled: true,
            dataType: "currency",
            render: (value, row, isExpanded) => {
                return (
                    <NestedTableQuoteItemColumn
                        row={row}
                        columnKey={"totalAnnualPrice"}
                        disabled={true}
                        hidden={isExpanded}
                    />
                )
            }
        },
        {
            key: "totalAnnualMargin",
            label: "Total Annual Margin %",
            header: <>%<br />Total Margin</>,
            className: "p-1",
            style: { minWidth: "110px", maxWidth: "110px" },
            dataType: "percentage",
            calcFooter: (footer, allData) => {
                // I used the recurring values, rather than annual.  This is because annual is derived from recurring.
                // If I use annual, I may get incorrect results in cases where there's a mix of term lengths.
                return footer.totalRecurringPrice > 0 ? RoundToTwoDecimalPlaces((footer.totalRecurringMarginValue / footer.totalRecurringPrice) * 100) : 0
            },
            render: (value, row, isExpanded) => {
                return (
                    <NestedTableQuoteItemColumn
                        row={row}
                        columnKey={"totalAnnualMargin"}
                        disabled={true}
                        hidden={isExpanded}
                    />
                )
            }
        },
        {
            key: "totalAnnualMarginValue",
            label: "Total Annual Margin £",
            header: <>£<br />Total Margin</>,
            canBeTotalled: true,
            dataType: "currency",
            render: (value, row, isExpanded) => {
                return (
                    <NestedTableQuoteItemColumn
                        row={row}
                        columnKey={"totalAnnualMarginValue"}
                        disabled={true}
                        hidden={isExpanded}
                    />
                )
            }
        },
        {
            key: "totalAnnualPriceDiscountPercent",
            label: "Total Annual Sell Discount %",
            header: <>%<br />Total Sell<br />Discount</>,
            className: "p-1",
            style: { minWidth: "110px", maxWidth: "110px" },
            dataType: "percentage",
            calcFooter: (footer, allData) => {
                // I used the recurring values, rather than annual.  This is because annual is derived from recurring.
                // If I use annual, I may get incorrect results in cases where there's a mix of term lengths.
                if (footer.totalRecurringPrice <= 0 || footer.totalOriginalRecurringPrice === 0) return 0;

                return ((RoundToTwoDecimalPlaces(footer.totalOriginalRecurringPrice - footer.totalRecurringPrice) / footer.totalOriginalRecurringPrice) * 100).toFixed(2)
            },
            render: (value, row, isExpanded) => {
                return (
                    <NestedTableQuoteItemColumn
                        row={row}
                        columnKey={"totalAnnualPriceDiscountPercent"}
                        disabled={true}
                        hidden={isExpanded}
                    />
                )
            }
        },
        {
            key: "totalAnnualPriceDiscountValue",
            label: "Total Annual Sell Discount £",
            header: <>£<br />Total Sell<br />Discount</>,
            canBeTotalled: true,
            dataType: "currency",
            render: (value, row, isExpanded) => {
                return (
                    <NestedTableQuoteItemColumn
                        row={row}
                        columnKey={"totalAnnualPriceDiscountValue"}
                        disabled={true}
                        hidden={isExpanded}
                    />
                )
            }
        }
    ].map(x => ({
        ...x, group: "Annual",
        className: "p-1",
        style: { ...x.style, minWidth: x.style?.minWidth || "165px", maxWidth: x.style?.maxWidth || "165px" }
    })), []);

    const contractColumns = useMemo(() => [
        {
            key: "totalContractCost",
            label: "Total Contract Cost",
            header: <>Total Cost</>,
            canBeTotalled: true,
            dataType: "currency",
            render: (value, row, isExpanded) => {
                return (
                    <NestedTableQuoteItemColumn
                        row={row}
                        columnKey={"totalContractCost"}
                        disabled={true}
                        hidden={isExpanded}
                    />
                )
            }
        },
        {
            key: "totalContractPrice",
            label: "Total Contract Sell",
            header: <>Total Sell</>,
            canBeTotalled: true,
            dataType: "currency",
            render: (value, row, isExpanded) => {
                return (
                    <NestedTableQuoteItemColumn
                        row={row}
                        columnKey={"totalContractPrice"}
                        disabled={true}
                        hidden={isExpanded}
                    />
                )
            }
        },
        {
            key: "totalContractMargin",
            label: "Total Contract Margin %",
            header: <>%<br />Total Margin</>,
            className: "p-1",
            style: { minWidth: "110px", maxWidth: "110px" },
            dataType: "percentage",
            calcFooter: (footer, allData) => {
                return footer.totalContractPrice > 0 ? RoundToTwoDecimalPlaces((footer.totalContractMarginValue / footer.totalContractPrice) * 100) : 0
            },
            render: (value, row, isExpanded) => {
                return (
                    <NestedTableQuoteItemColumn
                        row={row}
                        columnKey={"totalContractMargin"}
                        disabled={true}
                        hidden={isExpanded}
                    />
                )
            }
        },
        {
            key: "totalContractMarginValue",
            label: "Total Contract Margin £",
            header: <>£<br />Total Margin</>,
            canBeTotalled: true,
            dataType: "currency",
            render: (value, row, isExpanded) => {
                return (
                    <NestedTableQuoteItemColumn
                        row={row}
                        columnKey={"totalContractMarginValue"}
                        disabled={true}
                        hidden={isExpanded}
                    />
                )
            }
        },
        {
            key: "totalContractPriceDiscountPercent",
            label: "Total Contract Sell Discount %",
            header: <>%<br />Total Sell<br />Discount</>,
            className: "p-1",
            style: { minWidth: "110px", maxWidth: "110px" },
            dataType: "percentage",
            calcFooter: (footer, allData) => {
                const totalOriginalContractPrice = footer.totalOriginalUpfrontPrice + footer.totalOriginalRecurringPrice;
                const totalContractPrice = footer.totalUpfrontPrice + footer.totalRecurringPrice;

                return totalOriginalContractPrice
                    ? ((RoundToTwoDecimalPlaces(totalOriginalContractPrice - totalContractPrice) / totalOriginalContractPrice) * 100).toFixed(2)
                    : 0
            },
            render: (value, row, isExpanded) => {
                return (
                    <NestedTableQuoteItemColumn
                        row={row}
                        columnKey={"totalContractPriceDiscountPercent"}
                        disabled={true}
                        hidden={isExpanded}
                    />
                )
            }
        },
        {
            key: "totalContractPriceDiscountValue",
            label: "Total Contract Sell Discount £",
            header: <>£<br />Total Sell<br />Discount</>,
            canBeTotalled: true,
            dataType: "currency",
            render: (value, row, isExpanded) => {
                return (
                    <NestedTableQuoteItemColumn
                        row={row}
                        columnKey={"totalContractPriceDiscountValue"}
                        disabled={true}
                        hidden={isExpanded}
                    />
                )
            }
        }
    ].map(x => ({
        ...x, group: "Contract",
        className: "p-1",
        style: { ...x.style, minWidth: x.style?.minWidth || "165px", maxWidth: x.style?.maxWidth || "165px" }
    })), []);


    const actionsColumns = useMemo(() => ([
        {
            key: "disabled",
            label: "Included?",
            group: "Actions",
            isSystem: false,
            isDefault: true,
            isFixed: true,
            pinLocked: true,
            style: { width: "70px" },
            fixDirection: "right",
            className: "p-1 text-end",
            render: (_, row) => {
                return <NestedTableSwitchColumn
                	row={row}
                    propertyName="disabled"
                    invertValue={true}
                    quoteItems={quoteItems}
                    updateQuoteItem={updateQuoteItem}
                    updateQuoteItems={updateQuoteItems}
                 />
            }
        },
        {
        key: "actions",
        group: "Actions",
        label: "",
        settingsLabel: "Action Buttons",
        isSystem: true,
        isFixed: true,
        pinLocked: true,
        fixDirection: "right",
        style: { width: "130px"},
        className: "text-end",
        render: (value, row) => {
            return (
                <div className={`d-flex justify-content-end align-items-center gap-2 w-100  ${lockState?.isLocked ? "locked" : ""}`}>
                    {row.quoteModuleGuid && row.topLevelGroupRow ? (
                        <ModuleActionButtons
                            row={row}
                            onClickDecoupleModule={onClickDecoupleModule}
                            onClickDeleteModule={onClickDeleteModule}
                            onClickEditModule={onClickEditModule}
                        />
                    ) : (
                        !row.quoteModuleGuid &&
                        (
                            <>
                                {!row.topLevelGroupRow &&
                                    <>
                                        {row.source === "Basket Processing" &&
                                            <>
                                                <span
                                                    id={`automaticitem-${sanitizeForId(row.guid)}`}
                                                    title="Automatically generated items"
                                                    className="px-3"
                                                >
                                                    <i
                                                        className="bi bi-lightning">
                                                    </i>
                                                </span>
                                            </>
                                        }   
                                        {row.source !== "Basket Processing" &&                                  
                                            <Button
                                                id={`edit-btn-${sanitizeForId(row.guid)}`}
                                                color="secondary"
                                                outline
                                                title="Edit Item"
                                                onClick={() => onClickEditItem?.(row.guid)}
                                            >
                                                <i className="bi bi-pencil"></i>
                                            </Button>
                                        }
                                    </>
                                }
                                {row.topLevelGroupRow ?                                
                                    <>
                                        {row.items.find(item => item.source === "Basket Processing") &&
                                            <>
                                                <span
                                                    id={`automaticitem-${sanitizeForId(row.guid)}`}
                                                    title="Automatically generated items"
                                                    className="px-3"
                                                >
                                                    <i
                                                        className="bi bi-lightning">
                                                    </i>
                                                </span>
                                            </>
                                        }                                    
                                        {!row.items.find(item => item.source === "Basket Processing") &&
                                            <ActionButton
                                                id={`delete-group-btn-${sanitizeForId(row.guid)}`}
                                                color="danger"
                                                title="Delete Group"
                                                icon="bi-trash"
                                                onClick={() => onClickDeleteItemGroup(row.items)}
                                            />
                                        }
                                    </>
                                    :
                                    row.source !== "Basket Processing" &&
                                        <>
                                            <Button
                                                id={`delete-btn-${sanitizeForId(row.guid)}`}
                                                color="danger"
                                                    outline
                                                title="Delete Item"
                                                onClick={() => onClickDeleteItem?.(row.guid)}
                                            >
                                                <i className="bi bi-trash"></i>
                                            </Button>
                                        </>                                    
                                }
                            </>
                        )
                    )}
                </div>
            );
        }
    }]
), [lockState, onClickDecoupleModule, onClickEditModule, onClickDeleteModule, onClickEditItem, onClickDeleteItem, onClickDeleteItemGroup, quoteItems, updateQuoteItem, updateQuoteItems]);

    const columns = useMemo(() => [
        ...mainColumns,
        ...billingScheduleColumns,
        ...upfrontColumns,
        ...recurringColumns,
        ...annualColumns,
        ...contractColumns,
        ...customAttributeColumns,
        ...actionsColumns
    ].map(x => (
        { ...x, align: x.align || "right" }
    )), [mainColumns, billingScheduleColumns, upfrontColumns, recurringColumns, annualColumns, contractColumns, customAttributeColumns, actionsColumns])
    
    const inUseItemLookupTypes = useMemo(() => {
        const types = [];
        if (!itemLookupTypes) return types;
        quoteItems.forEach(item => {
            const typeInfo = itemLookupTypes.find(ilt => ilt.id === item.itemLookupTypeId);

            if (typeInfo && !types.find(t => t === typeInfo.itemLookupTypeName)) {
                types.push(typeInfo.itemLookupTypeName);
            }
        });
        return types;
    }, [quoteItems, itemLookupTypes]);

    // If the active tab's item type no longer has any items (e.g. after a bulk delete),
    // fall back to the Basket tab so the table doesn't show as empty with no explanation.
    useEffect(() => {
        const isFixedTab = currentTab === "Basket" || currentTab === "All Configuration";
        if (!isFixedTab && !inUseItemLookupTypes.includes(currentTab)) {
            setCurrentTab("Basket");
        }
    }, [inUseItemLookupTypes, currentTab]);

    const customAttributeKeys = useMemo(
        () => new Set(customAttributeColumns.map(col => col.key)),
        [customAttributeColumns]
    );

    const columnProfiles = useMemo(() => {
        const basketDefaults = new Set(["description", "quantity", "upfrontCost", "upfrontPrice", "totalUpfrontCost", "totalUpfrontPrice", "recurringCost", "recurringPrice", "totalRecurringCost", "totalRecurringPrice", "actions"]);
        const requiredKeys = new Set(["description", "actions"]);
        const configOnlyDefaults = new Set(["description", "actions"]);

        // Column groups that only make sense for upfront/recurring items.
        // Used to trim irrelevant columns from per-type profiles in the settings modal.
        const upfrontOnlyGroups = new Set(["Upfront"]);
        const recurringOnlyGroups = new Set(["Recurring", "Billing Schedule", "Annual"]);
        const contractGroups = new Set(["Contract"]);

        return {
            Basket: columns.map(col => ({
                key: col.key,
                // Custom attribute columns are hidden by default in Basket view
                isDefault: customAttributeKeys.has(col.key) ? false : basketDefaults.has(col.key),
                isRequired: requiredKeys.has(col.key)
            })),
            "All Configuration": columns.map(col => ({
                key: col.key,
                // Custom attribute columns are shown by default in Configuration view
                isDefault: customAttributeKeys.has(col.key) ? true : configOnlyDefaults.has(col.key),
                isRequired: requiredKeys.has(col.key)
            })),
            ...inUseItemLookupTypes.reduce((profiles, type) => {
                const typeAttrKeys = new Set(
                    customAttributeColumns
                        .filter(x => x.itemLookupTypes?.includes(type))
                        .map(col => col.key)
                );

                // Determine whether items of this type are upfront/recurring so we can
                // exclude irrelevant column groups from the settings modal entirely.
                const typeId = (itemLookupTypes ?? []).find(ilt => ilt.itemLookupTypeName === type)?.id;
                const itemsOfType = quoteItems.filter(item => item.itemLookupTypeId === typeId);
                const hasUpfront = itemsOfType.some(item => item.isUpfront);
                const hasRecurring = itemsOfType.some(item => item.isRecurring);

                profiles[type] = columns
                    .filter(col => {
                        const group = col.group ?? "";
                        if (upfrontOnlyGroups.has(group)) return hasUpfront;
                        if (recurringOnlyGroups.has(group)) return hasRecurring;
                        if (contractGroups.has(group)) return hasUpfront || hasRecurring;

                        // col is ANY custom attr → include only if it belongs to THIS type ✓
                        if (customAttributeKeys.has(col.key)) return typeAttrKeys.has(col.key);

                        return true; // main columns, actions, etc.
                    })
                    .map(col => ({
                        key: col.key,
                        isDefault: requiredKeys.has(col.key) || typeAttrKeys.has(col.key),
                        isRequired: requiredKeys.has(col.key)
                    }));
                return profiles;
            }, {})
        };
    }, [columns, customAttributeColumns, customAttributeKeys, inUseItemLookupTypes, quoteItems, itemLookupTypes]);  

    return (
        <>
            <CardHeader className={currentTab === 'Basket' ? "" : ""}>
                <Row>
                    <Col>
                        <div className="d-flex">
                            {['Basket', 'All Configuration', ...(inUseItemLookupTypes ?? []).sort((a, b) => a.localeCompare(b))].map((tab) =>
                                <h6
                                    key={tab}
                                    className={
                                        currentTab === tab
                                            ? "custom-nav-selected custom-nav m-3"
                                            : "custom-nav m-3"
                                    }
                                    style={{ cursor: "pointer" }}
                                    onClick={() => setCurrentTab(tab)}
                                >
                                    {tab}
                                </h6>
                            )}
                        </div>
                    </Col>
                    <Col className="text-end mt-3">
                        {layoutMode === "Vertical" ? (
                            <Button outline color={theme === "dark-mode" ? "dark" : "light"} onClick={() => setLayoutMode("Horizontal")}>
                                <i className="bi bi-layout-split fs-5"></i>
                            </Button>
                        ) : (
                            <Button outline color={theme === "dark-mode" ? "dark" : "light"} onClick={() => setLayoutMode("Vertical")}>
                                <i className="bi bi-view-stacked fs-5"></i>
                            </Button>
                        )}
                    </Col>
                </Row>
            </CardHeader>
            <CardBody className="mt-0 pt-0" style={{ overflow: "hidden", maxWidth: "100%", width: "100%" }}>
                {formattedData.length === 0 && (
                    <>
                        <div className="d-flex justify-content-end gap-2 mt-2">
                            <Button
                                key="add-custom-item"
                                className={`${lockState?.isLocked ? "locked" : ""}`}
                                onClick={onClickAddCustomItem}
                            >
                                Add Custom Item
                            </Button>
                        </div>
                        <div className="d-flex flex-column align-items-center justify-content-center" style={{ height: "150px" }}>
                            <i className="bi bi-basket fs-1 mb-3"></i>
                            <div className="fs-4">No items in basket</div>
                            <div>Please use the panel {layoutMode.toLocaleLowerCase() === "vertical" ? "to the left" : "below"} to add items.</div>
                        </div>
                    </>
                )}
                {formattedData.length > 0 && (<DataTableWindowed
                    id="quote-basket-table"
                    height="600px"
                    showNotesIcon
                    columns={columns}
                    data={formattedData}
                    isUpdating={isUpdating}
                    error={false}
                    paging={false}
                    shrinkHeightToContents={true}
                    onSelectedDataChange={(arr) => {
                        // We intentionally discard the row objects from `arr` and store only the IDs.
                        // The rows here come from DataTableWindowed's internal display-transformed data
                        // (customFilteredData), which can be stale at the point this callback fires —
                        // for example, immediately after a bulk save, the `data` prop change triggers
                        // this effect before customFilteredData has recomputed with the new values.
                        // By storing only IDs and deriving `selectedData` via useMemo from the live
                        // `quoteItems` prop, we guarantee the bulk modal always receives up-to-date rows.
                        setSelectedIds(new Set(arr.map(x => x.databaseId)));
                    }}                    
                    actions={[
                        <Button
                            key="add-custom-item"
                            className={`${lockState?.isLocked ? "locked" : ""}`}
                            onClick={onClickAddCustomItem}
                        >
                            Add Custom Item
                        </Button>,
                        /*
                        <ActionButton
                            key="empty-basket"
                            id="empty-basket-btn"
                            color="danger"
                            outline
                            title="Empty Basket"
                            icon="bi-trash"
                            label="Empty Basket"
                            busyLabel="Emptying..."
                            onClick={onClickEmptyBasket}
                            disabled={lockState?.isLocked || quoteItems.length === 0}
                            confirmTitle="Empty Basket?"
                            confirmMessage={
                                <div className="d-flex align-items-center">
                                    <i className="bi bi-exclamation-triangle-fill text-danger fs-1 me-3"></i>
                                    <div>
                                        <div className="mb-2">Are you sure you want to delete all quote items from the basket?</div>
                                        <div>This action cannot be undone.</div>
                                    </div>
                                </div>
                            }
                            confirmButtonLabel="Empty Basket"
                            confirmButtonColor="danger"
                        />,*/
                        <Button
                            key="update"
                            className={`${lockState?.isLocked ? "locked" : ""}`}
                            color={theme === "dark-mode" ? "dark" : "light"}
                            outline
                            disabled={selectedItems.length === 0}
                            onClick={() => {
                                setBulkModalOpen(true)
                            }}
                        >
                            Update Selected
                        </Button>                       
                    ]}
                    searchableFields={[
                        ...mainColumns.map(column => column.key),
                        ...customAttributeColumns.map(field => (
                            field.key
                        )),]}
                    showAutogeneratedFooter={true}
                    allowColumnCustomisation={true}
                    allowMultiSelect={!lockState?.isLocked}
                    columnProfiles={columnProfiles}
                    currentProfileKey={currentTab}
                />
                )}
            </CardBody>

            <QuoteItemBulkModal
                modalOpen={bulkModalOpen}
                setModalOpen={setBulkModalOpen}
                items={selectedItems}
                suppliers={suppliers || []}
                itemTypes={itemLookupTypes || []}
                onSave={handleBulkSave}
                showCommercialsTab={true}
            />


        </>
    );
};

export default QuoteCartTable;
